import {
  GridColumnMenu,
  GridColumnMenuProps,
  GridInitialState,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import React, {useState} from 'react';
import {addBreadcrumb, captureEvent} from '@sentry/react';
import {MenuItem} from '@mui/material';
import {Undo} from '@mui/icons-material';
import packageJson from '../../../package.json';
import {themeColors} from '../../theme/Theme';

export const useDataGridState = (tableId: string) => {
  const localStorageKey = `${tableId}-${packageJson?.version || 'development'}`;
  const apiRef = useGridApiRef();
  const [key, setKey] = useState(0);
  const [useLocalstorage, setUseLocalstorage] = useState(true);
  const [initialState, setInitialState] = React.useState<GridInitialState | false>();
  // false initial state means there is none, undefined means it hasnt loaded

  const saveSnapshot = React.useCallback(() => {
    if (apiRef?.current?.exportState && localStorage) {
      const currentState = apiRef.current.exportState();
      localStorage.setItem(localStorageKey, JSON.stringify(currentState));
    }
  }, [apiRef]);

  React.useLayoutEffect(() => {
    try {
      const stateFromLocalStorage = localStorage?.getItem(localStorageKey);
      const jsonState = stateFromLocalStorage ? JSON.parse(stateFromLocalStorage) : null;
      if (jsonState && useLocalstorage) {
        addBreadcrumb({
          message: 'Loading data grid state',
          data: {
            jsonState,
            localStorageKey,
            stateFromLocalStorage,
          },
        });
        setInitialState(jsonState);
      } else {
        setInitialState(false);
      }

      // handle refresh and navigating away/refreshing
      window.addEventListener('beforeunload', saveSnapshot);
    } catch (err) {
      setInitialState(false);
      captureEvent({message: 'Unable to load data grid state'});
    }
    return () => {
      try {
        // in case of an SPA remove the event-listener
        window.removeEventListener('beforeunload', saveSnapshot);
        saveSnapshot();
      } catch (err) {
        // do nothing
      }
    };
  }, [saveSnapshot]);

  function restoreState() {
    setUseLocalstorage(false);
    setKey(key + 1);
  }

  return {
    initialState,
    apiRef,
    key,
    restoreState,
  };
};

export const CustomColumnMenuComponent = (
  props: GridColumnMenuProps & {restoreState: () => void}
) => {
  const {hideMenu, colDef, children, restoreState, ...other} = props;

  return (
    <>
      <GridColumnMenu hideMenu={hideMenu} colDef={colDef} {...other}>
        {children}
      </GridColumnMenu>
      <MenuItem
        onClick={() => {
          restoreState();
        }}
      >
        <Undo htmlColor={themeColors.grey} sx={{mr: 2}} />
        Reset columns
      </MenuItem>
    </>
  );
};
